import React, { useState, useEffect } from 'react';
import { Plus, FileText, GitMerge, Lock, ChevronDown } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { api } from '../../api/client';
import Navbar from '../shared/Navbar';
import SectionEditor from './SectionEditor';
import CreateSectionModal from './CreateSectionModal';
import SessionVennView from './SessionVennView';

const WritingSessionView = ({ writingSession, onNavigateBack }) => {
  const { currentUser, presenterMode } = useAppContext();
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [activeView, setActiveView] = useState('sections');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const canEdit = currentUser.role === 'admin' || currentUser.role === 'facilitator';

  useEffect(() => {
    if (writingSession) loadSession();
  }, [writingSession]);

  useEffect(() => {
    if (sections.length > 0) {
      const urlParams = new URLSearchParams(window.location.search);
      const sectionId = urlParams.get('section');
      if (sectionId) {
        const section = sections.find(s => s.id === parseInt(sectionId));
        if (section) { setActiveSection(section); return; }
      }
      if (!activeSection) setActiveSection(sections[0]);
    }
  }, [sections]);

  useEffect(() => {
    if (activeSection && activeView === 'sections') {
      const url = new URL(window.location);
      url.searchParams.set('section', activeSection.id);
      window.history.replaceState({}, '', url);
    }
  }, [activeSection, activeView]);

  const loadSession = async () => {
    try {
      const data = await api.getWritingSession(writingSession.id);
      const sortedSections = (data.sections || []).sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.id - b.id;
      });
      setSections(sortedSections);
    } catch (error) {
      console.error('Failed to load writing session:', error);
    }
  };

  const handleLogout = () => { api.logout(); window.location.reload(); };

  const handleSectionCreated = (newSection) => {
    const updatedSections = [...sections, newSection].sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.id - b.id;
    });
    setSections(updatedSections);
    setActiveSection(newSection);
    setActiveView('sections');
    setShowCreateModal(false);
  };

  const handleSectionUpdate = (updatedSection) => {
    setSections(sections.map(s => s.id === updatedSection.id ? updatedSection : s));
    setActiveSection(updatedSection);
  };

  const navbarHeight = presenterMode ? 57 : 57;

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      <Navbar
        title={writingSession?.name}
        subtitle={writingSession?.description}
        onLogout={handleLogout}
        showBack
        onBack={onNavigateBack}
      />

      {/* Presenter mode section bar */}
      {presenterMode && (
        <div className="bg-white border-b-2 border-[#01006A] px-6 py-2 flex items-center gap-4 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <span className="text-[#01006A] font-bold text-xl truncate">
              {activeSection?.title || 'No section selected'}
            </span>
          </div>
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-4 py-1.5 bg-[#e6e6f5] text-[#01006A] border border-[#c0c0e6] rounded-lg text-sm font-semibold hover:bg-[#c0c0e6] transition-colors"
            >
              <span>Switch Section</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
                {sections.map((section, index) => (
                  <button
                    key={section.id}
                    onClick={() => { setActiveSection(section); setActiveView('sections'); setDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#e6e6f5] transition-colors ${
                      activeSection?.id === section.id ? 'bg-[#e6e6f5] font-semibold text-[#01006A]' : 'text-gray-800'
                    }`}
                  >
                    <span className="text-xs text-gray-400 w-5 flex-shrink-0">{index + 1}</span>
                    {section.is_password_protected && <Lock className="w-3 h-3 text-amber-400 flex-shrink-0" />}
                    <span className="text-sm truncate">{section.title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-1 min-h-0">
        {/* Sidebar -- hidden in presenter mode via CSS .w-64 rule */}
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-3 border-b border-gray-200 space-y-2 flex-shrink-0">
            {canEdit && (
              <button onClick={() => setShowCreateModal(true)}
                className="w-full flex items-center justify-center gap-2 bg-[#01006A] text-white px-3 py-2 rounded-lg hover:bg-[#010059] text-sm">
                <Plus className="w-4 h-4" />
                <span>Add Section</span>
              </button>
            )}
            <button
              onClick={() => setActiveView(activeView === 'venn' ? 'sections' : 'venn')}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                activeView === 'venn'
                  ? 'bg-[#01006A] text-white border-[#01006A] hover:bg-[#010059]'
                  : 'bg-white text-[#010059] border-[#6b6bc2] hover:bg-[#e6e6f5]'
              }`}>
              <GitMerge className="w-4 h-4" />
              <span>Venn Diagram</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {sections.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No sections yet</p>
              </div>
            )}
            {sections.map((section, index) => (
              <button key={section.id}
                onClick={() => { setActiveSection(section); setActiveView('sections'); }}
                className={`w-full text-left p-3 rounded-lg mb-1.5 transition-colors ${
                  activeSection?.id === section.id && activeView === 'sections'
                    ? 'bg-[#e6e6f5] border-2 border-[#01006A]'
                    : 'hover:bg-gray-50 border-2 border-transparent'
                }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {section.is_password_protected && (
                        <Lock className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      )}
                      <div className="font-medium text-sm text-gray-900 truncate">{section.title}</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5 capitalize">
                      {section.section_type.replace('_', ' ')}
                    </div>
                  </div>
                  <div className="text-xs text-gray-400 ml-1 flex-shrink-0">{index + 1}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden">
          {activeView === 'venn' ? (
            <SessionVennView writingSession={writingSession} sections={sections} />
          ) : activeSection ? (
            <SectionEditor
              section={activeSection}
              writingSession={writingSession}
              onSectionUpdate={handleSectionUpdate}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg">Select a section or create a new one to begin</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <CreateSectionModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        writingSessionId={writingSession?.id}
        foresightSessionId={writingSession?.foresight_session_id}
        onSectionCreated={handleSectionCreated}
        existingSectionsCount={sections.length}
      />
    </div>
  );
};

export default WritingSessionView;